import { ApiResponseError } from './ApiResponseError';

/**
 * Сколько раз повторять временный сбой.
 *
 * Два повтора — потолок ожидания порядка секунд. Держать человека дольше, чем он
 * готов ждать, хуже, чем честно показать отказ с возможностью повторить.
 */
export const TRANSIENT_RETRY_LIMIT = 2;

/**
 * Сбой временный, а не решение сервера.
 *
 * Граница по `5xx`: `4xx` бэкенд отдаёт ОСОЗНАННО (нет объекта, истекло, нет
 * прав, конфликт) — повтор ничего не изменит, а человек ждёт отказа втрое
 * дольше. `5xx` означает, что до решения дело не дошло. Сюда же попадает 503,
 * который клиент ставит САМ, когда запрос ушёл, а ответа не было вовсе
 * (`createApiClient.ts`, ветка без `error.response`) — то есть обрыв сети,
 * таймаут или отвергнутый origin.
 *
 * ## Почему это живёт в пакете, а не рядом с запросом
 *
 * Правило появилось в `deals` для превью приглашения (AF-397) и там же было
 * выведено adversarial-ревью: временный сбой шлюза, показанный как
 * «приглашение недействительно», отправляет человека искать письмо заново — при
 * живом приглашении. AF-537 принёс второй случай того же правила: стартовая
 * проба авторизации в `analyst`, где одна сетевая икота уводила ВЕСЬ экран в
 * `ErrorBoundary` (`useSuspenseQuery` + `retry: false`).
 *
 * Два места — уже не совпадение, а общее правило, и копия его бы убила: правку
 * внесли бы в одну, а разошлись бы обе. Предикат читает `ApiResponseError`,
 * который живёт здесь же, поэтому и место его — рядом с типом, а не в
 * приложении.
 *
 * ## Чего предикат НЕ решает
 *
 * Сколько ждать между попытками и что показывать после исчерпания: это решает
 * вызывающий, и решения там разные. `deals` показывает экран отказа с
 * перезагрузкой, `analyst` — общий `ErrorBoundary`, и это его отдельный долг.
 *
 * ## Почему параметр `unknown`, а не `ApiResponseError`
 *
 * TanStack Query типизирует ошибку как `Error`, но в `retry` попадает то, что
 * реально бросили, — а бросить могут что угодно: перехватчик, отклонивший
 * `undefined`, чужую ошибку из `queryFn`, строку. Аннотация типа этого не
 * остановит: она стирается при сборке. Обращение к `.code` у такого значения
 * рушится ВНУТРИ колбэка повторов, то есть в чужом стеке и без внятного
 * сообщения (найдено adversarial-ревью AF-537).
 *
 * ## Цена `instanceof`, названная вслух
 *
 * Предикат опознаёт ошибку по прототипу. Сегодня это надёжно: класс приезжает
 * из одного workspace-пакета, второй физической копии в графе нет (сверено
 * 24.09.2026). Но если копия когда-нибудь появится — пакет встроят в чужой
 * бандл, — ошибка из неё будет настоящей `ApiResponseError` на вид и чужой по
 * прототипу, и предикат ВЕРНЁТ `false`. Повторы замолчат, ничего не сломав
 * заметно: экран снова начнёт падать от сетевой икоты.
 *
 * Сторожа на это нет, и проверка по `error.name` вместо `instanceof` отвергнута
 * — она принимает любой объект, назвавшийся правильно, а это ровно то, от чего
 * защищает проверка класса.
 *
 * @example
 * retry: (failureCount, error) =>
 *   failureCount < TRANSIENT_RETRY_LIMIT && isTransientFailure(error),
 */
export const isTransientFailure = (error: unknown): boolean =>
  error instanceof ApiResponseError && (error.code ?? 0) >= 500;
